import { useRef, useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowLeft, ArrowRight, Play, Pause, Volume2, VolumeX, Maximize } from "lucide-react";
import { getProject, getNextProject } from "@/data/projects";
import { Reveal, LineReveal } from "@/components/Reveal";
import Footer from "@/sections/Footer";

const formatTime = (s) => {
    if (!Number.isFinite(s)) return "0:00";
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${String(sec).padStart(2, "0")}`;
};

const GalleryVideo = ({ item, projectName }) => {
    const wrapRef = useRef(null);
    const ref = useRef(null);
    const barRef = useRef(null);
    const seekingRef = useRef(false);
    const [playing, setPlaying] = useState(false);
    const [muted, setMuted] = useState(true);
    const [current, setCurrent] = useState(0);
    const [duration, setDuration] = useState(0);
    const clickToPlay = !!item.clickToPlay;
    const id = item.src.split("/").pop().split(".")[0];

    useEffect(() => {
        if (clickToPlay) return;
        const el = ref.current;
        if (!el) return;
        const io = new IntersectionObserver(
            ([e]) => {
                if (e.isIntersecting) el.play().catch(() => {});
            },
            { threshold: 0.2 }
        );
        io.observe(el);
        return () => io.disconnect();
    }, [clickToPlay]);

    const toggle = () => {
        const el = ref.current;
        if (!el) return;
        if (el.paused) {
            el.muted = false;
            setMuted(false);
            el.play().catch(() => {});
            setPlaying(true);
        } else {
            el.pause();
            setPlaying(false);
        }
    };

    const toggleMute = (e) => {
        e.stopPropagation();
        const el = ref.current;
        if (!el) return;
        el.muted = !el.muted;
        setMuted(el.muted);
    };

    const fullscreen = (e) => {
        e.stopPropagation();
        const wrap = wrapRef.current;
        const el = ref.current;
        if (document.fullscreenElement) {
            document.exitFullscreen();
        } else if (wrap?.requestFullscreen) {
            wrap.requestFullscreen().catch(() => {});
        } else if (el?.webkitEnterFullscreen) {
            el.webkitEnterFullscreen();
        }
    };

    const seekTo = (e) => {
        const el = ref.current;
        const bar = barRef.current;
        if (!el || !bar || !Number.isFinite(el.duration)) return;
        const rect = bar.getBoundingClientRect();
        const ratio = Math.min(Math.max((e.clientX - rect.left) / rect.width, 0), 1);
        el.currentTime = ratio * el.duration;
        setCurrent(el.currentTime);
    };

    const onSeekDown = (e) => {
        e.stopPropagation();
        seekingRef.current = true;
        e.currentTarget.setPointerCapture?.(e.pointerId);
        seekTo(e);
    };
    const onSeekMove = (e) => {
        if (seekingRef.current) seekTo(e);
    };
    const onSeekUp = (e) => {
        e.stopPropagation();
        seekingRef.current = false;
    };

    const progress = duration > 0 ? (current / duration) * 100 : 0;

    return (
        <div ref={wrapRef} className="group/video relative bg-[#0a1712]">
            <video
                ref={ref}
                autoPlay={!clickToPlay}
                muted
                loop
                playsInline
                preload="metadata"
                poster={item.poster}
                onClick={clickToPlay ? toggle : undefined}
                onTimeUpdate={(e) => !seekingRef.current && setCurrent(e.target.currentTime)}
                onLoadedMetadata={(e) => setDuration(e.target.duration)}
                onPlay={() => setPlaying(true)}
                onPause={() => setPlaying(false)}
                aria-label={`${projectName} — ${item.caption}`}
                className="aspect-video w-full object-contain"
            >
                {item.webm && <source src={item.webm} type="video/webm" />}
                <source src={item.src} type="video/mp4" />
            </video>
            {clickToPlay && (
                <>
                    <button
                        data-testid={`video-toggle-${id}`}
                        data-cursor="link"
                        onClick={toggle}
                        aria-label={playing ? "Video pausieren" : "Video abspielen"}
                        className={`absolute inset-0 flex items-center justify-center transition-opacity duration-300 ${
                            playing ? "opacity-0 hover:opacity-100" : "bg-[#132B21]/25 opacity-100"
                        }`}
                    >
                        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-[#E63946] transition-transform duration-300 hover:scale-110 sm:h-20 sm:w-20">
                            {playing ? (
                                <Pause size={22} className="text-[#F4F4F0]" fill="currentColor" />
                            ) : (
                                <Play size={22} className="ml-1 text-[#F4F4F0]" fill="currentColor" />
                            )}
                        </span>
                    </button>
                    <div
                        data-testid={`video-controls-${id}`}
                        onClick={(e) => e.stopPropagation()}
                        className={`absolute inset-x-0 bottom-0 z-10 bg-gradient-to-t from-[#0a1712]/90 to-transparent px-3 pb-2.5 pt-8 transition-opacity duration-300 sm:px-4 ${
                            playing ? "opacity-0 focus-within:opacity-100 group-hover/video:opacity-100" : "opacity-100"
                        }`}
                    >
                        <div
                            ref={barRef}
                            data-testid={`video-seekbar-${id}`}
                            data-cursor="link"
                            role="slider"
                            aria-label="Video spulen"
                            aria-valuemin={0}
                            aria-valuemax={Math.round(duration)}
                            aria-valuenow={Math.round(current)}
                            onPointerDown={onSeekDown}
                            onPointerMove={onSeekMove}
                            onPointerUp={onSeekUp}
                            className="group/bar relative flex h-4 w-full cursor-pointer items-center"
                        >
                            <div className="relative h-[3px] w-full overflow-hidden rounded-full bg-white/20 transition-[height] duration-200 group-hover/bar:h-[5px]">
                                <div
                                    className="absolute inset-y-0 left-0 rounded-full bg-[#E63946]"
                                    style={{ width: `${progress}%` }}
                                />
                            </div>
                            <div
                                className="pointer-events-none absolute top-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#E63946] opacity-0 transition-opacity duration-200 group-hover/bar:opacity-100"
                                style={{ left: `${progress}%` }}
                            />
                        </div>
                        <div className="mt-1.5 flex items-center justify-between">
                            <span className="font-mono text-[10px] tracking-wider text-[#F4F4F0]/80">
                                {formatTime(current)} / {formatTime(duration)}
                            </span>
                            <div className="flex gap-2">
                                <button
                                    data-testid={`video-sound-${id}`}
                                    data-cursor="link"
                                    onClick={toggleMute}
                                    aria-label={muted ? "Ton einschalten" : "Ton ausschalten"}
                                    className="flex h-9 w-9 items-center justify-center rounded-full bg-[#132B21]/80 text-[#F4F4F0] backdrop-blur transition-colors duration-300 hover:bg-[#E63946]"
                                >
                                    {muted ? <VolumeX size={15} /> : <Volume2 size={15} />}
                                </button>
                                <button
                                    data-testid={`video-fullscreen-${id}`}
                                    data-cursor="link"
                                    onClick={fullscreen}
                                    aria-label="Vollbild"
                                    className="flex h-9 w-9 items-center justify-center rounded-full bg-[#132B21]/80 text-[#F4F4F0] backdrop-blur transition-colors duration-300 hover:bg-[#E63946]"
                                >
                                    <Maximize size={15} />
                                </button>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default function ProjectPage() {
    const { slug } = useParams();
    const project = getProject(slug);
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const yCover = useTransform(scrollYProgress, [0, 1], [0, 160]);

    if (!project) return <Navigate to="/" replace />;
    const next = getNextProject(slug);

    return (
        <main data-testid="project-page" className="pt-28 sm:pt-36">
            <div className="mx-auto max-w-[1600px] px-5 sm:px-10">
                <Link
                    to="/"
                    data-testid="project-back-link"
                    data-cursor="link"
                    className="mb-10 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-[#A1A1AA] transition-colors duration-300 hover:text-[#E63946]"
                >
                    <ArrowLeft size={14} /> Alle Arbeiten
                </Link>

                <p className="font-mono text-[11px] uppercase tracking-[0.35em] text-[#E63946]">{project.category}</p>
                <h1 className="mt-3 font-display font-extrabold uppercase leading-[0.9] tracking-tighter">
                    <LineReveal>
                        <span className="text-[8vw] text-[#F4F4F0] sm:text-[7vw]">{project.name}</span>
                    </LineReveal>
                </h1>

                <Reveal delay={0.2}>
                    <div className="mt-10 grid grid-cols-2 gap-6 border-t border-white/10 pt-6 sm:grid-cols-3" data-testid="project-meta">
                        <div>
                            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#A1A1AA]">Kategorie</p>
                            <p className="mt-2 text-sm text-[#F4F4F0]">{project.category}</p>
                        </div>
                        <div>
                            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#A1A1AA]">Jahr</p>
                            <p className="mt-2 text-sm text-[#F4F4F0]">{project.year}</p>
                        </div>
                        <div>
                            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#A1A1AA]">Auftraggeber</p>
                            <p className="mt-2 text-sm text-[#F4F4F0]">{project.client}</p>
                        </div>
                    </div>
                    <p className="mt-10 max-w-2xl text-lg font-light leading-relaxed text-[#F4F4F0]/90 sm:text-xl">
                        {project.description}
                    </p>
                </Reveal>
            </div>

            <div ref={ref} className="mt-16 overflow-hidden sm:mt-24">
                <motion.img
                    style={{ y: yCover }}
                    src={project.images[0].src}
                    alt={`${project.name} — ${project.images[0].caption}`}
                    className="h-[60vh] w-full object-contain sm:h-[85vh]"
                />
            </div>

            <div className="mx-auto max-w-[1600px] space-y-16 px-5 py-20 sm:space-y-28 sm:px-10 sm:py-32">
                {project.images.map((img, i) => (
                    <Reveal key={img.src + i}>
                        <figure className={i % 2 === 0 ? "sm:mr-auto sm:w-[72%]" : "sm:ml-auto sm:w-[72%]"}>
                            <div className="overflow-hidden">
                                {img.video ? (
                                    <GalleryVideo item={img} projectName={project.name} />
                                ) : (
                                    <img
                                        src={img.src}
                                        alt={`${project.name} — ${img.caption}`}
                                        loading="lazy"
                                        className="w-full transition-transform duration-[1200ms] ease-out hover:scale-[1.03]"
                                    />
                                )}
                            </div>
                            <figcaption className="mt-4 flex items-center justify-between border-t border-white/10 pt-3">
                                <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#A1A1AA]">{img.caption}</span>
                                <span className="font-mono text-[11px] text-[#E63946]">{String(i + 1).padStart(2, "0")} / {String(project.images.length).padStart(2, "0")}</span>
                            </figcaption>
                        </figure>
                    </Reveal>
                ))}
            </div>

            <Link
                to={`/projekt/${next.slug}`}
                data-testid="next-project-link"
                data-cursor="view"
                className="group block border-t border-white/10"
            >
                <div className="mx-auto max-w-[1600px] px-5 py-20 sm:px-10 sm:py-28">
                    <p className="font-mono text-[11px] uppercase tracking-[0.35em] text-[#A1A1AA]">Nächstes Projekt</p>
                    <div className="mt-4 flex items-center justify-between gap-6">
                        <span className="font-display text-4xl font-extrabold uppercase leading-none tracking-tighter text-[#F4F4F0] transition-colors duration-500 group-hover:text-[#E63946] sm:text-7xl lg:text-8xl">
                            {next.name}
                        </span>
                        <ArrowRight
                            size={48}
                            className="shrink-0 text-[#A1A1AA] transition-all duration-500 group-hover:translate-x-3 group-hover:text-[#E63946]"
                        />
                    </div>
                </div>
            </Link>

            <Footer />
        </main>
    );
}
